import React from "react";
import { FeatureArticleRichText } from "web/react/components/feature-article/feature-article-rich-text";
import { FeatureArticleFeaturedProduct } from "web/react/components/feature-article/types";
import { withHardBreaks } from "web/react/components/feature-article/utils";
import ProductCard from "web/react/components/product-card/react/product-card";
import { ProductCardLink } from "web/react/components/product-card/react/product-card-link";
import { Button } from "web/react/emo/button";
import { HStack } from "web/react/emo/h-stack";
import { Heading } from "web/react/emo/heading";
import {
    ProductCardDetails,
    ProductCardPrice,
    ProductCardWishlist,
    ProductImage,
} from "web/react/emo/product-card";
import { Stack } from "web/react/emo/shared/components/stack";
import { VStack } from "web/react/emo/v-stack";
import * as styles from "./feature-article-featured-products.css";

interface FeatureArticleFeaturedProductsProps {
    items: FeatureArticleFeaturedProduct[];
    isSponsored?: boolean;
}

/**
 * A row of "featured" products: each is a shoppable product card (Catalog-enriched) paired
 * with an editorial content column (title, spec bullets and a "Shop on Lyst" / "Buy Now" CTA).
 */
export function FeatureArticleFeaturedProducts({
    items,
    isSponsored = false,
}: FeatureArticleFeaturedProductsProps): React.ReactElement {
    return (
        <div className={styles.grid}>
            {items.map((item, index) => (
                <FeaturedItem
                    key={`${item.buy_option_id}-${index}`}
                    item={item}
                    isSponsored={isSponsored}
                />
            ))}
        </div>
    );
}

function FeaturedItem({
    item,
    isSponsored,
}: {
    item: FeatureArticleFeaturedProduct;
    isSponsored: boolean;
}): React.ReactElement | null {
    const { product, title, long_description: longDescription } = item;

    // Nothing to show without a resolved product — skip the whole item.
    if (!product) {
        return null;
    }

    return (
        <ProductCard product={product}>
            <Stack
                spacing="sm"
                direction={{ sm: "column", md: "row" }}
                align={{ sm: "stretch", md: "start" }}
            >
                <div className={styles.card}>
                    <VStack spacing="sm">
                        <ProductImage shouldRenderLeadLinkInImage={isSponsored} />
                        <VStack spacing="xxs">
                            <HStack justify="space-between">
                                <ProductCardPrice size="lg" />
                                <ProductCardWishlist isSavedForLater={product.is_saved_for_later} />
                            </HStack>
                            <ProductCardDetails shouldRenderLeadLink={isSponsored} />
                        </VStack>
                    </VStack>
                </div>

                <div className={styles.content}>
                    <VStack spacing="md">
                        <Heading as="h3" textStyle="hero-s">
                            {title}
                        </Heading>
                        {longDescription && (
                            <FeatureArticleRichText>
                                {withHardBreaks(longDescription)}
                            </FeatureArticleRichText>
                        )}
                    </VStack>
                    <ProductCardLink reason="cta" predicate={isSponsored}>
                        <Button
                            variant="primary"
                            size="lg"
                            width="full"
                            title={isSponsored ? "Buy Now" : "Shop on Lyst"}
                        />
                    </ProductCardLink>
                </div>
            </Stack>
        </ProductCard>
    );
}
